import type { IntlShape } from 'react-intl';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import type { CardContext } from '@atlaskit/link-provider/types';
import type { EmojiNodeDataProvider } from '@atlaskit/editor-plugin-emoji/EmojiNodeDataProvider';
import { AI_POLISH_MENU_ITEM } from '@atlaskit/editor-common/block-menu';
import type { MentionNodeDataProvider } from '@atlaskit/editor-common/mention';
import type { UserPreferencesProvider as UserPreferencesProviderNext } from '@atlaskit/editor-common/user-preferences-provider';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { ContextualFormattingEnabledOptions } from '@atlaskit/editor-common/toolbar/types';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility/types';
import type { FindReplaceOptions } from '@atlaskit/editor-plugins/find-replace';
import type { TaskDecisionProvider } from '@atlaskit/task-decision/types';
import type { MediaPluginOptions } from '@atlaskit/editor-plugins/media';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode/editorViewmodePluginType';
import type { PasteOptionsToolbarPluginConfiguration } from '@atlaskit/editor-plugin-paste-options-toolbar/paste-options-toolbar-plugin-type';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import type { MentionProvider } from '@atlaskit/mention/types';
import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory/context-identifier-provider';
import type { AutoformattingProvider } from '@atlaskit/editor-common/provider-factory/autoformatting-provider';
import type { MentionPluginConfig } from '@atlaskit/editor-plugins/mentions';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension/extensionPluginType';
import type { LimitedModePluginOptions } from '@atlaskit/editor-plugin-limited-mode/limited-mode-plugin-type';
import type { LoomPluginOptionsWithoutProvider } from '@atlaskit/editor-plugin-loom/types';
import type { MentionsChangedHandler } from '@atlaskit/editor-plugin-mentions/types';
import type { EngagementPlatformPluginOptions } from '@atlaskit/editor-plugin-engagement-platform/engagementPlatformPluginType';
import type { ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel/contextPanelPluginType';
import type { SelectionExtensionPluginOptions } from '@atlaskit/editor-plugin-selection-extension/types';
import type { DiffParams } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';
import type { SyncedBlockFeedbackContext } from '@atlaskit/editor-plugin-synced-block/synced-block-plugin-type';
import type { SyncedBlockProvider } from '@atlaskit/editor-synced-block-provider/syncBlockProvider';
import { getSyncedBlockRenderer } from '@atlaskit/editor-synced-block-renderer/get-synced-block-renderer';
import type { UserPreferencesProvider } from '@atlaskit/editor-common/types/user-preferences';
import type { LinkingOptions } from '@atlaskit/editor-common/types/hyperlink';
import type { EmptyStateHandler } from '@atlaskit/editor-common/types/empty-state-handler';
import type { QuickInsertOptions } from '@atlaskit/editor-common/types/quick-insert';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import type { AnnotationProviders } from '@atlaskit/editor-plugins/annotation';
import type { CollabEditOptions } from '@atlaskit/editor-common/collab';
import type { TrackChangesPluginOptions } from '@atlaskit/editor-plugin-track-changes/track-changes-plugin-type';
import { mediaEditingPlugin } from '@atlaskit/editor-plugin-media-editing/media-editing-plugin';

import { fullPagePreset, type FullPagePresetProps } from '@atlassian/confluence-presets/full-page';
import type { DuplicateMauiAppSuccess } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';
import { nativeEmbedsPlugin } from '@atlassian/editor-plugin-native-embeds/native-embeds-plugin';
import { snippetsPlugin, type SnippetsPluginOptions } from '@atlassian/editor-plugin-snippets';
import type { AIDefinitionsPluginOptions } from '@atlassian/editor-plugin-ai-definitions/types';
import type { AIPluginOptions } from '@atlassian/editor-plugin-ai/aiPluginType';
import { aiAutocompletePlugin } from '@atlassian/editor-plugin-ai-autocomplete/ai-autocomplete-plugin';
import { aiSelectionContextPlugin } from '@atlassian/editor-plugin-ai-selection-context/aiSelectionContextPlugin';
import type { AiSuggestionsPluginOptions } from '@atlassian/editor-plugin-ai-suggestions';
import { malleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui/malleableUiPlugin';
import { companyHubSectionCtaPlugin } from '@atlassian/editor-plugin-company-hub-section-cta/companyHubSectionCtaPlugin';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';
import { forgeEditorPlugin } from '@atlassian/xen-editor-provider/editor-plugin';

import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup.ts';
import { CREATE_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { nativeTabsToolbarPlugin } from '@confluence/editor-plugin-native-tabs-toolbar/entry-points/nativeTabsToolbarPlugin.ts';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { getUnsupportedContent } from '@confluence/fabric-extension-lib/entry-points/getUnsupportedContent.ts';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import { fg } from '@confluence/feature-gating';
import type { BlockHandleClickHandler } from '@confluence/table-to-database/entry-points/useTableBlockHandleClick.ts';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage/entry-points/editorOfflineStoragePlugin.ts';
import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links/entry-points/headingLinksPlugin.ts';
import { floatingTableOfContentsPlugin } from '@confluence/floating-table-of-contents-common/entry-points/floatingTableOfContentsPlugin.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { createRemixContentComponent } from '@confluence/malleable-ui-common/entry-points/EditorRemixCreator.ts';
import { CommonRemixHiddenOptionsResolver } from '@confluence/malleable-ui-common/entry-points/useShouldHideDiagramRemixOption.ts';
import { isRemixOptedOut } from '@confluence/remix-availability/entry-points/isRemixOptedOut.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { createConfluenceMauiDiagramPasteConfig } from '../utils/createConfluenceMauiDiagramPasteConfig.ts';
import { createMalleableUiProductFlags } from '../utils/createMalleableUiProductFlags.ts';
import { getCodeBlockFormatProvider } from '../utils/codeBlockFormatProvider.ts';
import { fullPageQuickInsertRecommendedItems } from '../utils/quickInsertRecommendedItems.ts';

import { conflictResolutionUIPlugin } from './plugins/conflictResolutionUIPlugin/index.ts';
import { guardRedactionFlagPlugin } from './plugins/guardRedactionFlagPlugin/guardRedactionFlagPlugin.tsx';
import { aiPlannerRemoteTextHighlightPlugin } from './plugins/aiPlannerBridgeHighlightPlugin.ts';
import { emptyStatePlugin } from './plugins/emptyStatePlugin.ts';
import { ssrPlugin } from './plugins/ssrPlugin.ts';
import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin.ts';
import { getSyncedBlockHeadingAnchorLinks } from './syncedBlockHeadingAnchorLinks.ts';

/**
 * IMPORTANT: Don't use optional properties in this interface because it reduces the type safety.
 *            Use `undefined` instead to indicate that the property is not set.
 */
export interface CreateFullPageEditorPresetProps {
	__isRemixHidden?: boolean;
	__remixDisabledReason: 'archived' | 'version-history' | undefined;
	__livePage: boolean | undefined;
	__rendererExtensionOptions: ExtensionPluginOptions['__rendererExtensionOptions'] | undefined;
	allowFindReplace: boolean | FindReplaceOptions | undefined;
	annotationProviders: Pick<AnnotationProviders, 'annotationManager' | 'inlineComment'> | undefined;
	appearance: FullPageAppearances;
	autoformattingProvider: Promise<AutoformattingProvider> | undefined;
	collabEdit: CollabEditOptions | undefined;
	contentId: string | undefined;
	pasteOptionsToolbarPluginOptions: PasteOptionsToolbarPluginConfiguration | undefined;
	spaceKey: string | undefined;
	contextIdentifierProvider: Promise<ContextIdentifierProvider> | undefined;
	contextPanelOptions: Pick<ContextPanelPluginOptions, 'objectSideBar'> | undefined;
	editorEngagementPlatformPluginConfig: Pick<EngagementPlatformPluginOptions, 'coordinationClient'>;
	editorPluginAIDefinitionsConfig: AIDefinitionsPluginOptions;
	editorPluginAIDefinitionsEnabled: boolean;
	editorPluginAIOptions?: AIPluginOptions;
	elementBrowser:
		| {
				emptyStateHandler?: EmptyStateHandler;
		  }
		| undefined;
	emojiProvider: Promise<EmojiProvider> | undefined;
	emojiNodeDataProvider: EmojiNodeDataProvider | undefined;
	mentionNodeDataProvider: MentionNodeDataProvider | undefined;
	extensionHandlers: ExtensionHandlers | undefined;
	flags: FlagsStateContainer | undefined;
	getExtensionHeight: GetPMNodeHeight | undefined;
	getHeadingLinkPath: () => string | null;
	shouldHideHeadingAnchorLinks?: boolean;
	handleMentionsChanged: MentionsChangedHandler;
	currentUserId: string | undefined;
	getIsRovoPanelOpen?: () => boolean;
	onAgentMentionChatClick?: (
		agentId: string,
		agentMentionContext?: import('@atlaskit/adf-schema/doc').DocNode,
	) => void;
	hasEditPermission: boolean | undefined;
	hasRequestedEditPermission: boolean | undefined;
	intl: IntlShape;
	isAIEnabledForContent: boolean;
	isCompanyHub: boolean;
	isDesktop: boolean;
	isUnpublishedDraft: boolean;
	isLoomPluginEnabled: boolean;
	limitedModePluginOptions: LimitedModePluginOptions | undefined;
	smartCardContext?: CardContext;
	enableAgentSectioning?: boolean;
	linking: Pick<LinkingOptions, 'linkPicker' | 'smartLinks'> | undefined;
	loomPluginOptions: Pick<LoomPluginOptionsWithoutProvider, 'renderButton'>;
	media:
		| Pick<
				MediaPluginOptions,
				| 'provider'
				| 'syncProvider'
				| 'mediaViewerExtensions'
				| 'fallbackMediaNameFetcher'
				| 'onMediaRenderEvent'
		  >
		| undefined;
	mention: Pick<MentionPluginConfig, 'profilecardProvider'> | undefined;
	mentionProvider: Promise<MentionProvider> | undefined;
	prevAppearance: FullPageAppearances | undefined;
	quickInsert: QuickInsertOptions | undefined;
	referentialityContext: ReferentialityContext;
	requestToEditContent: (() => void) | undefined;
	selectionExtensionPluginOptions: SelectionExtensionPluginOptions;
	snippetsPluginOptions: SnippetsPluginOptions | undefined;
	taskDecisionProvider: Promise<TaskDecisionProvider> | undefined;
	userPreferencesProvider: UserPreferencesProvider | undefined;
	userPreferencesProviderNext: UserPreferencesProviderNext | undefined;
	viewMode: ViewMode | undefined;
	contextualFormattingEnabled: boolean;
	contextualFormattingMode?: ContextualFormattingEnabledOptions;
	enableAIPlannerRemoteTextHighlight: boolean | undefined;
	disabled?: boolean;
	blockLinkHashPrefix: string | undefined;
	showDiffPluginParams: DiffParams | undefined;
	syncedBlockProvider: SyncedBlockProvider | undefined;
	syncedBlockEventHandlers: EventHandlers | undefined;
	onTableBlockHandleClick: BlockHandleClickHandler | undefined;
	onFeedbackPromptShown?: () => Promise<void> | void;
	onGiveFeedback?: (context: SyncedBlockFeedbackContext) => void;
	shouldShowFeedbackPrompt?: () => Promise<boolean> | boolean;
	disableFloatingToolbar: boolean | undefined;
	disableToolbar: boolean | undefined;
	trackChangesPluginOptions: TrackChangesPluginOptions | undefined;
	contentMode: EditorContentMode | undefined;
	aiSuggestionsPluginOptions: AiSuggestionsPluginOptions;
	enablePlaceholderLoadingSpinner?: boolean;
	isEmbeddedConfluence: boolean;
	isRovoLLMEnabled: boolean;
	/** Product AI eligibility; omitted values disable suggested edits. */
	isAdminHubAIEnabled?: boolean;
	isAIEarlyAccessEnabled?: boolean;
	duplicateMauiApp?: (
		appId: string,
		containerContentId: string,
		contentType?: string,
	) => Promise<DuplicateMauiAppSuccess>;
	contentType?: string;
	cloudId?: string;
}

/**
 * This function creates a full page editor preset with the provided options.
 *
 * If you want to do any changes, first of all, read https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4239829169/Editor+RFC+059+Editor+Presets+Package.
 *
 * HOW TO MAKE CHANGES:
 * 1. Do not use optional properties in the `CreateFullPageEditorPresetProps` interface.
 * 2. Keep this function as small and simple as possible. Pass only the necessary parameters to the plugin options:
 *   - If a plugin parameter doesn't need any data from Confluence (e.g. option is just a static value),
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - If a plugin parameter depends on Statsig experiment value, FG value,
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - Pass parameters from this function to the plugin options ONLY if there is NO OTHER WAY to do it directly in the plugin options.
 *   - Pass only NECESSARY parameters to the plugin options, AVOID passing the WHOLE OBJECT with all properties.
 *   - Do NOT USE SPREAD operator (`...props`) to pass plugin options, it can make the code less readable and harder to maintain.
 */
// FIXME: https://product-fabric.atlassian.net/browse/ED-25722

type FullPagePluginOptionsWithOptionalAI = Omit<FullPagePresetProps['pluginOptions'], 'ai'> & {
	ai?: FullPagePresetProps['pluginOptions']['ai'];
};

const allowOptionalAIPluginOptions = (
	pluginOptions: FullPagePluginOptionsWithOptionalAI,
): FullPagePresetProps['pluginOptions'] => pluginOptions as FullPagePresetProps['pluginOptions'];

type FullPageProvidersWithFormatCodeProvider = FullPagePresetProps['providers'] & {
	formatCodeProvider?: ReturnType<typeof getCodeBlockFormatProvider>;
};

const allowFormatCodeProvider = (
	providers: FullPageProvidersWithFormatCodeProvider,
): FullPagePresetProps['providers'] => providers as FullPagePresetProps['providers'];

export function createFullPageEditorPreset(options: CreateFullPageEditorPresetProps): any {
	const { intl, appearance, prevAppearance } = options;
	const createCommentExperience: typeof createInlineCommentCompoundExperience = {
		...createInlineCommentCompoundExperience,
		start: ({ attributes }) =>
			createInlineCommentCompoundExperience.start({
				attributes: { ...attributes, pageMode: options.__livePage ? 'live' : 'edit' },
			}),
	};

	const isNativeEmbeddedLiveDoc =
		!!options.__livePage &&
		!!options.isEmbeddedConfluence &&
		UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);

	// Hiding remix leaves malleableUiPlugin registered, so existing remixes still
	// render — it only removes the toolbar, block menu, paste menu, right-side
	// decoration and floating toolbar entrypoints for generating or editing one.
	const isRemixHidden = options.__isRemixHidden || isRemixOptedOut();
	const isEditorAIPluginEnabled = Boolean(options.editorPluginAIOptions);
	const shouldAddMalleableUiPlugin =
		UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
		!options.isEmbeddedConfluence;
	const hasRemixInBlockMenu =
		shouldAddMalleableUiPlugin &&
		!!options.isRovoLLMEnabled &&
		!isRemixHidden &&
		options.__remixDisabledReason === undefined;
	const malleableUiProductFlags = createMalleableUiProductFlags(options.flags);
	const agentManagedExtensionPluginOption = {
		agentManagedExtension: {
			cloudId: options.cloudId,
			contentType: options.contentType,
		},
	};

	// `prettier-ignore` keeps the long fluent chain below on its current
	// `fullPagePreset(...)` form. Without it Prettier re-indents ~570
	// chained-method lines and explodes the diff for any unrelated change.
	// The chain is purely declarative configuration.
	// prettier-ignore
	const preset = (
		fullPagePreset({
			intl,
			isRovoLLMEnabled: options.isRovoLLMEnabled,
			pluginOptions: allowOptionalAIPluginOptions({
				contentFormat: {
					initialContentMode: options.contentMode ?? 'standard',
				},
				emoji: {
					contentId: options.contentId,
				},
				helpDialog: {
					imageUploadProviderExists: false,
					aiEnabled: options.isAIEnabledForContent ?? false,
				},
				hyperlink: {
					editorAppearance: appearance,
					linkPicker: options.linking?.linkPicker,
					onClickCallback: options.linking?.smartLinks?.onClickCallback,
				},
				quickInsert: {
					emptyStateHandler: options.elementBrowser?.emptyStateHandler,
					quickInsert: options.quickInsert,
					...fullPageQuickInsertRecommendedItems,
				},
				placeholder: {
					viewMode: options.viewMode,
					isAIEnabled: options.isAIEnabledForContent ?? false,
					// hide placeholder when (1) variant1 is enabled (2) not embedded editor & (3) user would see BPQA.
					// match BPQA conditions as defined by shouldRenderBPQA in FullPageEditorComponent
					// in this case, only isCompanyHub is not handled yet by the placeholder plugin
					isPlaceholderHidden:
						!options.isEmbeddedConfluence &&
						!options.isCompanyHub &&
						options.isRovoLLMEnabled &&
						expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1'),
					enableLoadingSpinner: options.enablePlaceholderLoadingSpinner,
					isRovoLLMEnabled: options.isRovoLLMEnabled,
				},
				selection: {
					__livePage: options.__livePage,
				},
				...agentManagedExtensionPluginOption,
				editorDisabled: {
					disabled: options.disabled,
				},
				breakout: {
					editorAppearance: appearance,
				},
				expand: {
					editorAppearance: appearance,
					__livePage: options.__livePage,
				},
				annotation: {
					annotationManager: options.annotationProviders?.annotationManager,
					createCommentExperience,
					selectCommentExperience: selectInlineCommentCompoundExperience,
					viewInlineCommentTraceUFOPress: () => {
						// Similar to logic in next/packages/annotation-provider-inline-comments/src/renderer/ViewComponent.tsx
						// but for editor:
						// DO NOT start this trace if this is viewing a just-created comment, in
						// which case an `editor-create-inline-comment` action trace would still be
						// running. We let it run to cover the entire create UI flow end-to-end.
						const createExperienceState = getExperienceTracker().getExperienceState(
							CREATE_INLINE_COMMENT_EXPERIENCE,
						);
						const skipTrace = createExperienceState?.hasStopped === false;
						if (!skipTrace) {
							traceUFOPress('view-inline-comment-action');
						}
					},
				},
				media: {
					createCommentExperience,
					editorAppearance: appearance,
					mediaViewerExtensions: options.media?.mediaViewerExtensions,
					fallbackMediaNameFetcher: options.media?.fallbackMediaNameFetcher,
					onMediaRenderEvent: options.media?.onMediaRenderEvent,
				},
				mentions: {
					enableAgentSectioning: options.enableAgentSectioning,
					showAgentMentionsLabsLozenge: options.enableAgentSectioning,
					handleMentionsChanged: options.handleMentionsChanged,
					currentUserId: options.currentUserId,
					mentionNodeDataProvider: options.mentionNodeDataProvider,
					...(expValEquals('platform_editor_agent_mentions', 'isEnabled', true) && {
						getIsRovoPanelOpen: options.getIsRovoPanelOpen,
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						...(fg('platform_editor_agent_mentions_drop_one_fixes') && {
							onAgentMentionChatClick: options.onAgentMentionChatClick,
						}),
					}),
				},
				table: {
					__livePage: options.__livePage,
					editorAppearance: appearance,
					prevEditorAppearance: prevAppearance,
				},
				tasksAndDecisions: {
					hasEditPermission: options.hasEditPermission,
					requestToEditContent: options.requestToEditContent,
					hasRequestedEditPermission: options.hasRequestedEditPermission,
				},
				collabEdit: {
					collabEdit: options.collabEdit,
					__livePage: options.__livePage,
				},
				// Remove the options.contextPanelOptions undefined checking after cleaning `platform_editor_ai_object_sidebar_injection` FG
				// and options.contextPanelOptions will be always defined.
				contextPanel: options.contextPanelOptions
					? {
							objectSideBar: options.contextPanelOptions.objectSideBar,
						}
					: undefined,
				extension: {
					extensionHandlers: options.extensionHandlers,
					editorAppearance: appearance,
					__rendererExtensionOptions: options.__rendererExtensionOptions,
					getExtensionHeight: options.getExtensionHeight,
					getUnsupportedContent,
				},
				layout: {
					editorAppearance: appearance,
				},
				card: {
					smartCardContext:
						isSSRStreaming()
							? options.smartCardContext
							: undefined,
					editorAppearance: appearance,
					linkPicker: options.linking?.linkPicker,
					onClickCallback: options.linking?.smartLinks?.onClickCallback,
					__livePage: options.__livePage,
					CompetitorPrompt,
					enablePasteDisplayAsMenu: true,
				},
				insertBlock: {
					editorAppearance: appearance,
					toolbarButtons: {
						emoji: { enabled: true, showAt: 'lg' },
						insert: { enabled: true, showAt: 'lg' },
						layout: { enabled: true, showAt: 'lg' },
						media: { enabled: true, showAt: 'lg' },
						mention: { enabled: true, showAt: 'lg' },
						table: { enabled: true, showAt: 'md' },
						taskList: { enabled: true, showAt: 'lg' },
					},
				},
				avatarGroup: {
					// Avatars are moved to Confluence codebase for Edit in Context
					// When Edit in Context is enabled primaryToolbarComponents is undefined
					// For more details please check
					// https://hello.atlassian.net/wiki/spaces/PCG/pages/2851572180/Editor+toolbar+for+live+pages+and+edit+in+context+projects
					collabEdit: options.collabEdit,
				},
				referentiality: {
					referentialityContext: options.referentialityContext,
				},
				...(options.editorPluginAIOptions
					? {
							ai: {
								...options.editorPluginAIOptions,
								...(hasRemixInBlockMenu
									? { disableBlockMenuActions: [AI_POLISH_MENU_ITEM.key] }
									: {}),
							},
						}
					: {}),
				pasteOptionsToolbar: options.pasteOptionsToolbarPluginOptions,
				loom: {
					renderButton: options.loomPluginOptions.renderButton,
				},
				editorViewMode: {
					viewMode: options.viewMode,
				},
				aiDefinitions: options.editorPluginAIDefinitionsConfig,
				selectionMarker: {
					__livePage: options.__livePage ?? false,
				},
				engagementPlatform: {
					coordinationClient: options.editorEngagementPlatformPluginConfig.coordinationClient,
				},
				selectionExtension: options.selectionExtensionPluginOptions,
				metrics: { userPreferencesProvider: options.userPreferencesProvider },
				primaryToolbar: {
					contextualFormattingEnabled: options.contextualFormattingEnabled,
				},
				selectionToolbar: {
					contextualFormattingEnabled: options.contextualFormattingEnabled,
					// In embedded live doc, the toolbar is always unpinned and the pin option is hidden
					disablePin: isNativeEmbeddedLiveDoc,
				},
				userPreferencesPlugin: {
					// In embedded live doc, force the toolbar to start unpinned regardless of any saved user preference
					initialToolbarDockingPosition: isNativeEmbeddedLiveDoc ? 'none' : undefined,
				},
				blockControls: {
					onBlockHandleClick: expVal('confluence_table_to_database', 'isEnabled', false)
						? options.onTableBlockHandleClick
						: undefined,
				},
				blockMenu: {
					getLinkPath: options.getHeadingLinkPath,
					blockLinkHashPrefix: options.blockLinkHashPrefix,
				},
				showDiff: options.showDiffPluginParams,
				syncedBlock:
					options.syncedBlockProvider
						? {
								__livePage: options.__livePage,
								enableSourceCreation: true,
								syncedBlockRenderer: getSyncedBlockRenderer({
									syncBlockRendererOptions: {
										eventHandlers: options.syncedBlockEventHandlers,
										mentionNodeDataProvider: options.mentionNodeDataProvider,
										stickyHeaders: {
											show: true,
											offsetTop: 0,
										},
										allowHeadingAnchorLinks: getSyncedBlockHeadingAnchorLinks(
											options.getHeadingLinkPath,
										),
									},
								}),
								syncBlockDataProvider: options.syncedBlockProvider,
								onFeedbackPromptShown: options.onFeedbackPromptShown,
								onGiveFeedback: options.onGiveFeedback,
								shouldShowFeedbackPrompt: options.shouldShowFeedbackPrompt,
							}
						: undefined,
				limitedMode: options.limitedModePluginOptions || {},
				toolbar: {
					contextualFormattingEnabled: options.contextualFormattingMode ?? 'controlled',
				},
				trackChanges: {
					ButtonWrapper: options.trackChangesPluginOptions?.ButtonWrapper,
				},
				aiSuggestions: options.aiSuggestionsPluginOptions,
				aiImageGeneration: {
					isRemixHidden,
					isRovoLLMEnabled: options.isRovoLLMEnabled,
				}
			}),
			providers: allowFormatCodeProvider({
				// In embedded live doc, we always want the toolbar to be unpinned regardless of saved preferences.
				// Passing undefined for both providers prevents any shared user preference state from affecting
				// the embedded live doc toolbar docking position.
				userPreferencesProvider: isNativeEmbeddedLiveDoc
					? undefined
					: options.userPreferencesProvider,
				userPreferencesProviderNext: isNativeEmbeddedLiveDoc
					? undefined
					: options.userPreferencesProviderNext,
				cardProvider: options.linking?.smartLinks?.provider,
				contextIdentifierProvider: options.contextIdentifierProvider,
				inlineCommentAnnotationProvider: options.annotationProviders?.inlineComment,
				mediaProvider: options.media?.provider,
				syncMediaProvider: options.media?.syncProvider,
				mentionProvider: options.mentionProvider,
				profilecardProvider: options.mention?.profilecardProvider,
				emojiProvider: options.emojiProvider,
				emojiNodeDataProvider: options.emojiNodeDataProvider,
				formatCodeProvider: getCodeBlockFormatProvider(),
				collabEditProvider: options.collabEdit?.provider,
				autoformattingProvider: options.autoformattingProvider,
				taskDecisionProvider: options.taskDecisionProvider,
			}),
			enabledOptionalPlugins: {
				agentManagedExtension:
					options.isRovoLLMEnabled &&
					expValEquals('agent-managed_blocks_mvp', 'isEnabled', true),
				blockCollapse:
					expValEquals('platform_editor_block_control_migration', 'isEnabled', true) &&
					expValEquals('platform_editor_collapsible_headings', 'isEnabled', true),
				limitedMode: false,
				findReplace: Boolean(options.allowFindReplace),
				referentiality: Boolean(options.referentialityContext.plugin),
				loom: options.isLoomPluginEnabled,
				aiDefinitions: isEditorAIPluginEnabled && options.editorPluginAIDefinitionsEnabled,
				aiImageGeneration:
					isEditorAIPluginEnabled &&
					expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
					isFeatureEnabled('confluence', 'confluence-ai'),
				connectivity: true,
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				metrics: fg('platform_editor_ease_of_use_metrics'),
				codeBlockAdvanced: true,
				selectionExtension: true,
				aiExperience: Boolean(
					isEditorAIPluginEnabled &&
						options.editorPluginAIOptions?.aiGlobalOptIn.status !== 'disabled',
				),
				interaction: true,
				userPreferences: true,
				trackChanges: true,
				showDiff: true,
				toolbar: !options?.disableToolbar,
				blockMenu: true,
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				localId: fg('platform_editor_adf_with_localid'),
				aiStreamingOrchestrator: true,
				syncedBlock: true,
				floatingToolbar: !options?.disableFloatingToolbar,
				contentFormat: true,
				uiControlRegistry: expValEquals('platform_editor_paste_actions_menu', 'isEnabled', true),
				aiSuggestions:
					isEditorAIPluginEnabled &&
					options.isRovoLLMEnabled &&
					options.isAdminHubAIEnabled === true &&
					options.isAIEarlyAccessEnabled === true &&
					(expVal('platform_editor_ai_content_suggested_edits', 'isEnabled', false) ||
						// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Rollout gate for the platformised subset of suggested edits, independent of the experiment.
						fg('platform_editor_ai_suggested_edits')),
				collabEdit: true,
				numberedHeadings: true,
			},
		})
			.add(ssrPlugin)
			.maybeAdd(floatingTableOfContentsPlugin, expValEquals('platform_editor_floating_toc', 'isEnabled', true))
			// When hiding content titles in URLs, skip the heading anchor links plugin to prevent generating anchor URLs that expose UGC.
			.maybeAdd(
				[
					EditorHeadingLinksPlugin,
					{
						getHeadingLinkPath: options.getHeadingLinkPath,
					},
				],
				!options.shouldHideHeadingAnchorLinks,
			)
			.add(pageTitleUtilsPlugin)
			.add(fabricExtensionManagerPlugin)
			.add(forgeEditorPlugin)
			.add([
				editorOfflineStoragePlugin,
				{
					flags: options.flags,
					hasEditPermission: options.hasEditPermission,
					shouldSkipOfflineVersionForUnpublishedDraft: options.isUnpublishedDraft,
				},
			])
			.add([
				conflictResolutionUIPlugin,
				{
					flags: options.flags,
					contentId: options.contentId,
					spaceKey: options.spaceKey,
				},
			])
			.maybeAdd(
				aiPlannerRemoteTextHighlightPlugin,
				Boolean(options.enableAIPlannerRemoteTextHighlight),
			)
			.maybeAdd(
				[guardRedactionFlagPlugin, { flags: options.flags, contentId: options.contentId }],
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				fg('confluence_guard_redaction_flag'),
			)
			.add(emptyStatePlugin)
			.add(legacyContentMacroPlugin)
			.maybeAdd(nativeTabsToolbarPlugin, expVal('confluence_native_tabs_experiment', 'isEnabled', false))
			.maybeAdd(mediaEditingPlugin, expVal('platform_editor_add_image_editing', 'isEnabled', false))
			.maybeAdd(companyHubSectionCtaPlugin, options.isCompanyHub)
			.maybeAdd(
				[
					malleableUiPlugin,
					{
						__isRemixHidden: isRemixHidden,
						__isRovoLLMEnabled: options.isRovoLLMEnabled,
						__remixDisabledReason: options.__remixDisabledReason,
						__livePage: options.__livePage,
						shouldHideToolbarWhileRemixing: true,
						createRemixContentComponent,
						remixHiddenOptionsResolver: CommonRemixHiddenOptionsResolver,
						productFlags: malleableUiProductFlags,
						diagramPaste:
							options.cloudId && options.spaceKey
								? createConfluenceMauiDiagramPasteConfig({
										contentId: options.contentId,
										contentType: options.contentType,
										duplicateMauiApp: options.duplicateMauiApp,
										siteId: options.cloudId,
										spaceKey: options.spaceKey,
										userId: options.currentUserId,
									})
								: undefined,
					},
				],
				shouldAddMalleableUiPlugin,
			)
			.maybeAdd(
				aiSelectionContextPlugin,
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				shouldAddMalleableUiPlugin && fg('maui_remixable_page_native_embeds'),
			)
			.maybeAdd(
				[
					nativeEmbedsPlugin,
					{
						// Preload the editor comment component when a MAUI Comment action is shown.
						onCommentButtonMount: options.annotationProviders?.inlineComment?.onCommentButtonMount,
					},
				],
				UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) ||
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					fg('platform_native_embeds_rollout_non_maui_experience'),
			)
			.maybeAdd(
				aiAutocompletePlugin,
				expVal('platform_editor_ai_headingautocomplete', 'isEnabled', false),
			)
	);

	if (
		options.snippetsPluginOptions &&
		expValEquals('platform_editor_blocks_conf', 'isEnabled', true) &&
		expValEquals('platform_editor_snippets_plugin_confluence', 'isEnabled', true)
	) {
		return preset.add([snippetsPlugin, options.snippetsPluginOptions]);
	}

	return preset;
}
